import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { type AppUser, logout, onAuthChange } from '../services/auth';

// Sign in and sign up are deliberately not on this context: they are captcha
// gated (Soulstack Verify) and each needs a fresh widget token from the form
// that calls them (ScreenAuth, in app/screens-app.tsx, via services/auth.ts).
// A context method with no token to pass would be a way around that gate.
interface AuthContextValue {
  user: AppUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Dev-only local identity: when no Cognito pool is configured in a dev build,
// the SPA signs in a local creator, mirroring the API's development-tenant
// behaviour (requests without a token attribute to the dev tenant). Inert in
// production builds and whenever Cognito is configured.
const DEV_AUTH = import.meta.env.DEV && !import.meta.env.VITE_COGNITO_USER_POOL_ID;
const DEV_USER: AppUser = {
  id: 'dev-local',
  email: 'dev@sstk.local',
  displayName: 'Dev Creator',
  emailVerified: true,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(DEV_AUTH ? DEV_USER : null);
  const [loading, setLoading] = useState(!DEV_AUTH);

  useEffect(() => {
    if (DEV_AUTH) return;
    const unsub = onAuthChange((next) => {
      setUser(next);
      setLoading(false);
    });
    return unsub;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      signOut: async () => {
        if (DEV_AUTH) {
          setUser(null);
          return;
        }
        await logout();
      },
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider.');
  return ctx;
}
